// @ts-nocheck

import * as S from './styles';
import { useFormGeneric, useAutoSaveContext } from '~/hooks';
import { Checkbox, Input, Select } from 'semantic-ui-react';
import DatePicker from 'react-datepicker';
import { CSSProperties, ReactNode, useMemo } from 'react';
import FieldErrorMessage from './FieldErrorMessage';
import FieldLabel from './FieldLabel';
import { HTMLInputTypeAttribute } from 'react';
import { parseISO, format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

export type FormSchemaAwareWidgetProps = {
  as?: any
  autoComplete?: string | boolean
  schemaKey: string
  checkbox?: boolean
  datepicker?: boolean
  options?: any
  label?: ReactNode // label for checkboxes
  fieldStyle?: CSSProperties
  fieldLabel?: ReactNode
  fieldLabelStyle?: CSSProperties
  helpText?: string
  helpTextStyle?: CSSProperties
  optional?: boolean
  multiple?: boolean
  icon?: string
  iconPosition?: 'left' | 'right'
  inline?: boolean
  onChange?: Function
  onBlur?: Function
  search?: boolean
  style?: any
  maxSelections?: any
  selection?: boolean
  placeholder?: string
  fluid?: boolean
  maxLength?: number
  checked?: boolean
  readOnly?: any
  rows?: string | number
  type?: HTMLInputTypeAttribute
  errorText?: string
  allowAdditions?: boolean
  selectOnBlur?: boolean
  additionLabel?: string
  onAddItem?: Function
  disabled?: boolean
  neverNull?: boolean // This is for preventing a selector from being cleared (because clearable is the default)
}

export interface SchemaAwareWidgetImplProps extends FormSchemaAwareWidgetProps {
  context?: any
  children?: any
}
function SchemaAwareWidgetImpl({
  context,
  as: Widget,
  schemaKey,
  checkbox,
  datepicker,
  options,
  fieldStyle,
  fieldLabel,
  fieldLabelStyle,
  helpText,
  helpTextStyle,
  optional,
  inline,
  onChange,
  children,
  maxSelections,
  errorText,
  upward,
  neverNull,
  ...props
}: SchemaAwareWidgetImplProps) {
  let { updateValue, handleOnChangeEvent, getValue, errors } = context;
  let value = getValue(schemaKey);

  // Convert PostgreSQL text array format or strings to proper arrays for multiselect
  if (props.multiple && typeof value === 'string' && value) {
    // Handle PostgreSQL text array format: {"value1","value2"}
    if (value.startsWith('{') && value.endsWith('}')) {
      value = value.slice(1, -1) // Remove outer braces
        .split(',')
        .map((v: string) => v.trim().replace(/^"|"$/g, '')) // Remove quotes
        .filter((v: string) => v);
    }
  }

  if (typeof value === 'boolean') {
    checkbox = true;
  }

  const callback = useMemo(
    () => {
      if (onChange) {
        updateValue = onChange;
        handleOnChangeEvent = ({ target: { value } }: any) => onChange(schemaKey, value);
      }
      if (datepicker) {
        return (value: any) => {
          const formattedDate = format(value, 'yyyy-MM-dd'); // Format the date as YYYY-MM-DD
          updateValue(schemaKey, formattedDate); // Send only the date portion
        };
      } else if (options) {
        return (key: string, { value }: any) => {
          // For multiselect, ensure value is saved as JSON array
          if (props.multiple && Array.isArray(value)) {
            updateValue(schemaKey, value);
          } else {
            updateValue(schemaKey, value);
          }
        };
      } else if (checkbox) {
        return () => updateValue(schemaKey, !getValue(schemaKey));
      } else {
        return (ev: any) => handleOnChangeEvent(ev, schemaKey);
      }
    },
    [schemaKey, onChange, !!datepicker, !!options, !!checkbox]
  );

  if (!Widget) {
    // noinspection JSUnusedAssignment
    Widget = options ? Select : (checkbox ? Checkbox : Input);
  }

  const error = errorText ?? errors[schemaKey as string];

  return (
    <S.SchemaAwareWidgetField
      error={!!error}
      inline={inline}
      style={{ ...fieldStyle }}
    >
      <FieldLabel
        text={fieldLabel}
        htmlFor={schemaKey}
        style={fieldLabelStyle}
        optional={optional}
        helpText={helpText}
      />
      {datepicker ? (
        <DatePicker {...props}
          selected={
            value
              ? utcToZonedTime(parseISO(value), 'UTC') // Convert the ISO string to a UTC-based date
              : null
          }
          onChange={callback}
          placeholderText={props?.placeholder ?? '––/––/––––'}
          calendarClassName="datepicker-calendar"
          portalId="root"
        />
      ) : (
        <Widget {...props}
          id={schemaKey}
          name={schemaKey}
          onChange={(_e: any, data: any) => {
            if (maxSelections !== undefined && data?.value?.length && maxSelections < data?.value?.length) {
              return;
            }
            callback(_e, data);
          }}
          value={checkbox ? undefined : (value == null ? (props.multiple ? [] : '') : value)}
          checked={checkbox ? !!value : undefined}
          options={options?.filter((option: any) => !(option.deprecated && option.value !== value))}
          {...(Widget === Select ? { upward: false } : {})}
          // disabling neverNull because it is causing autosave issues
          // {...(Widget === Select ? { upward: false, clearable: true } : {})}
          // {...(neverNull ? { clearable: false } : {})}
        >
          {children}
        </Widget>
      )}
      <FieldErrorMessage content={error} />
    </S.SchemaAwareWidgetField>
  );
}



// this could be much more concise by relying entirely on ...props, but doing this facilitates IDE autocompletion
export function FormSchemaAwareWidget({
  as, schemaKey, checkbox, datepicker, options, fieldStyle, fieldLabel, fieldLabelStyle,
  helpText, helpTextStyle, optional, inline, onChange, maxSelections, ...props
}: FormSchemaAwareWidgetProps) {
  return (
    <SchemaAwareWidgetImpl
      context={useFormGeneric()}
      {...{
        as, schemaKey, checkbox, datepicker, options, fieldStyle, fieldLabel, fieldLabelStyle,
        helpText, helpTextStyle, optional, inline, onChange, maxSelections
      }}
      {...props}
    />
  );
}

// this could be much more concise by relying entirely on ...props, but doing this facilitates IDE autocompletion
export function AutoSaveSchemaAwareWidget({
  as, schemaKey, checkbox, datepicker, options, fieldStyle, fieldLabel, fieldLabelStyle,
  helpText, helpTextStyle, optional, inline, onChange, maxSelections, ...props
}: FormSchemaAwareWidgetProps) {
  return (
    <SchemaAwareWidgetImpl
      context={useAutoSaveContext()}
      {...{
        as, schemaKey, checkbox, datepicker, options, fieldStyle, fieldLabel, fieldLabelStyle,
        helpText, helpTextStyle, optional, inline, onChange, maxSelections
      }}
      {...props}
    />
  );
}
